import { Box } from '@lifeomic/chroma-react/components/Box';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { defineMessages, useIntl } from 'react-intl';
import { sharedMessages } from '../sharedIntlLabels';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { BarChart2, Zap } from '@lifeomic/chromicons';
import { BuildTableData } from '../FilterResultsManager/types';

import {
  getCKB,
  getGene,
  getExpression,
  getIgv,
  getAvg,
  getMax,
  getMedian,
  getMin,
  getQ1,
  getQ3,
  getStddev,
} from '../FilterResultsManager/ExpressionsContent/buildTableData';
import { useSharedDetailsStyles } from './sharedDetailsStyles';
import { useSharedStyles } from '../FilterResultsManager/sharedStyles';
import * as React from 'react';
import clsx from 'clsx';

const messages = defineMessages({
  summary: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.ExpressioNDetails.summary',
    defaultMessage: 'Summary',
  },
  projectSamples: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.ExpressioNDetails.projectSamples',
    defaultMessage: 'Project Samples',
  },
});

interface ExpressionDetailsProps
  extends Omit<BuildTableData, 'build' | 'classes' | 'formatMessage'> {
  variant?: unknown;
}

export const ExpressionDetails: React.FC<ExpressionDetailsProps> = ({
  account,
  activeSequence,
  variant,
  variantSet,
}) => {
  const classes = useSharedDetailsStyles({});
  const sharedClasses = useSharedStyles({});

  const { formatMessage } = useIntl();

  return (
    <Box className={classes.root}>
      <Box direction="column">
        <Paper className={clsx(classes.paper, classes.oneCol)}>
          <Box className={classes.subhead}>
            <Zap className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.summary)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.jax)}</dt>
            <dd>{getCKB(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.gene)}</dt>
            <dd>{getGene(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.expression)}</dt>
            <dd>{getExpression(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.igv)}</dt>
            <dd>
              {getIgv({
                activeSequence,
                account,
                classes: sharedClasses,
                formatMessage,
                value: variant,
                variantSet,
              })}
            </dd>
          </dl>
        </Paper>
      </Box>

      <Box direction="column">
        <Paper className={classes.paper}>
          <Box className={classes.subhead}>
            <BarChart2 className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.projectSamples)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.avg)}</dt>
            <dd>{getAvg(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.stddev)}</dt>
            <dd>{getStddev(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.min)}</dt>
            <dd>{getMin(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.q1)}</dt>
            <dd>{getQ1(variant)}</dd>
          </dl>
          <dl>
            <dt>{formatMessage(sharedMessages.median)}</dt>
            <dd>{getMedian(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.q3)}</dt>
            <dd>{getQ3(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.max)}</dt>
            <dd>{getMax(variant)}</dd>
          </dl>
        </Paper>
      </Box>
    </Box>
  );
};
